/**
 * Help page: tutorial videos and the technical-support form.
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ManagementPage } from '@/components/common/ManagementPage';
import { SlidePanel } from '@/components/common/SlidePanel';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { Button } from '@/components/forms/Button';
import { HelpSupportBanner } from '@/components/tutorials/HelpSupportBanner';
import { EditHelpSupportFormView } from '@/components/tutorials/EditHelpSupportFormView';
import { CreateTutorialFormView } from '@/components/tutorials/CreateTutorialFormView';
import { EditTutorialFormView } from '@/components/tutorials/EditTutorialFormView';
import { TutorialsGrid } from '@/components/tutorials/TutorialsGrid';
import { TutorialVideoModal } from '@/components/tutorials/TutorialVideoModal';
import { tutorialKeys } from '@/components/tutorials/tutorial-configs';
import { helpSupportFallback } from '@/components/tutorials/tutorial-form-utils';
import { formatTutorialArchiveModalItemName } from '@/components/tutorials/tutorial-grid-utils';
import { toast } from '@/components/ui/toast';
import { useMode } from '@/contexts/ModeContext';
import { usePageActions } from '@/hooks/usePageActions';
import { deleteTutorialVideo, getHelpSupport } from '@/services/tutorials.service';
import type { TutorialVideo } from '@/types/tutorial.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** Catalog video held in page state, or none. */
type TutorialSelection = TutorialVideo | null;
/** Edit-panel id, or none. */
type TutorialEditId = string | null;

/** Help hub: tutorial catalog plus the mode-specific technical-support form. */
export function TutorialsPage(): React.ReactElement {
  const { mode } = useMode();
  const { canCreate, canEditRow, canDeleteRow } = usePageActions('tutorials');
  const { canEditRow: canEditHelpSupport } = usePageActions('help-support');
  const queryClient = useQueryClient();
  const [selectedVideo, setSelectedVideo] = React.useState<TutorialSelection>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editId, setEditId] = React.useState<TutorialEditId>(null);
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<TutorialSelection>(null);

  const catalogMode = mode ?? 'remote';

  const helpQuery = useQuery({
    queryKey: tutorialKeys.help('web', catalogMode),
    queryFn: () => getHelpSupport('web', catalogMode),
    enabled: mode !== null,
  });

  const help = helpQuery.data ?? helpSupportFallback('web', catalogMode);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: tutorialKeys.all });
    void queryClient.invalidateQueries({ queryKey: tutorialKeys.helpRoot });
  };

  const deleteMutation = useMutation({
    mutationFn: deleteTutorialVideo,
    onSuccess: () => {
      toast.success('Vidéo archivée');
      setToDelete(null);
      invalidate();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleCloseModal = () => setSelectedVideo(null);
  const handleOpenCreate = () => setCreateOpen(true);
  const handleCloseCreate = () => setCreateOpen(false);
  const handleOpenHelp = () => setHelpOpen(true);
  const handleCloseHelp = () => setHelpOpen(false);
  const handleCloseEdit = () => setEditId(null);
  const handleCloseDelete = () => setToDelete(null);
  const handleSaved = () => {
    setCreateOpen(false);
    setEditId(null);
    setHelpOpen(false);
    invalidate();
  };
  const handleConfirmDelete = () => {
    if (!toDelete) return;
    deleteMutation.mutate(toDelete.id);
  };

  function renderAddButton(): React.ReactNode {
    if (!canCreate) return null;
    return (
      <Button preset="create-entity" onClick={handleOpenCreate}>
        Ajouter une vidéo
      </Button>
    );
  }

  return (
    <ManagementPage
      title="Tutoriels et aide"
      subtitle="Guides vidéo et ressources pour vous accompagner dans l'utilisation de la plateforme"
      actions={renderAddButton()}
    >
      <TutorialsGrid
        mode={mode}
        canEdit={canEditRow}
        canDelete={canDeleteRow}
        onWatch={setSelectedVideo}
        onEdit={setEditId}
        onDelete={setToDelete}
      />
      <HelpSupportBanner
        formUrl={help.url}
        phone={help.phone}
        canEdit={canEditHelpSupport}
        onEdit={handleOpenHelp}
      />

      <TutorialVideoModal video={selectedVideo} open={!!selectedVideo} onClose={handleCloseModal} />

      <SlidePanel isOpen={createOpen} onClose={handleCloseCreate}>
        {createOpen ? (
          <CreateTutorialFormView onClose={handleCloseCreate} onSuccess={handleSaved} />
        ) : null}
      </SlidePanel>

      <SlidePanel isOpen={!!editId} onClose={handleCloseEdit}>
        {editId ? (
          <EditTutorialFormView
            videoId={editId}
            onClose={handleCloseEdit}
            onSuccess={handleSaved}
          />
        ) : null}
      </SlidePanel>

      <SlidePanel isOpen={helpOpen} onClose={handleCloseHelp}>
        {helpOpen ? (
          <EditHelpSupportFormView onClose={handleCloseHelp} onSuccess={handleSaved} />
        ) : null}
      </SlidePanel>

      <DeleteModal
        open={!!toDelete}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        mode="archive"
        itemName={formatTutorialArchiveModalItemName(toDelete)}
        isLoading={deleteMutation.isPending}
      />
    </ManagementPage>
  );
}
